import { useCallback, useEffect, useState } from "react";
import { CheckCheck, CheckCircle2, ListChecks, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { SectionCard } from "./SectionShell";
import { readJson } from "@/hooks/admin/adminApi";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

export const useApprovals = (authFetch, enabled, onChanged) => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState([]);
  const load = useCallback(async () => {
    if (!enabled) return;
    try {
      const res = await authFetch("/admin/pending-approvals");
      if (res.ok) {
        const data = await res.json();
        setRows(data);
        setSelected((s) => s.filter((id) => data.some((r) => r.id === id)));
      }
    } catch (err) {
      console.warn("pending approvals load failed", err);
    }
  }, [authFetch, enabled]);
  useEffect(() => { load(); }, [load]);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () =>
    setSelected((s) => {
      const ids = rows.map((r) => r.id);
      return ids.length > 0 && ids.every((id) => s.includes(id)) ? [] : ids;
    });

  const approve = async (r) => {
    if (!window.confirm(`Approve ${r.name}? They will be moved to Registrations and receive the welcome WhatsApp + email now.`)) return;
    setBusy(r.id);
    try {
      const res = await authFetch(`/admin/pending-approvals/${r.id}/approve`, { method: "POST" });
      const data = await readJson(res);
      if (!res.ok || data?.ok === false) throw new Error(data?.detail || "Failed");
      setRows((x) => x.filter((y) => y.id !== r.id));
      setSelected((s) => s.filter((id) => id !== r.id));
      toast.success(`${r.name} approved — welcome ${data.welcome_wa_status === "sent" ? "sent on WhatsApp and email" : "email sent (WhatsApp " + (data.welcome_wa_status || "skipped") + ")"}`);
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not approve");
    } finally {
      setBusy(null);
    }
  };

  const bulk = async (path, body, label, count) => {
    if (!count || !window.confirm(`Approve ${label}? Each will be moved to Registrations and receive the welcome WhatsApp + email.`)) return;
    setBusy("bulk");
    try {
      const res = await authFetch(path, { method: "POST", body: body ? JSON.stringify(body) : undefined });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data?.detail || "Failed");
      toast.success(`${data.approved} participant(s) approved and notified`);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not approve");
    } finally {
      setBusy(null);
    }
  };
  const approveAll = () => bulk(`/admin/pending-approvals/approve-all`, null, `all ${rows.length} pending participant(s)`, rows.length);
  const approveSelected = () => bulk(`/admin/pending-approvals/approve-selected`, { ids: selected }, `the ${selected.length} selected participant(s)`, selected.length);

  const remove = async (r) => {
    if (!window.confirm(`Remove ${r.name} from Approval Pending? They will NOT be registered or notified.`)) return;
    try {
      const res = await authFetch(`/admin/pending-approvals/${r.id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed");
      setRows((x) => x.filter((y) => y.id !== r.id));
      setSelected((s) => s.filter((id) => id !== r.id));
      toast.success("Removed");
    } catch (err) {
      toast.error(err.message || "Could not remove");
    }
  };

  return { rows, busy, selected, toggleSelect, toggleAll, approve, approveAll, approveSelected, remove, reload: load };
};

const COLS = ["#", "Name", "Mobile", "WhatsApp", "Email", "Profession", "Place", "Program", "Registered", ""];

export const ApprovalPendingSection = ({ ap }) => {
  const bulkBusy = ap.busy === "bulk";
  return (
    <SectionCard id="approval" testId="section-approval">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-muted-foreground max-w-xl">Participants who have already done the program earlier wait here. Tick the ones you want and approve them together, or approve one by one / all at once. Approving moves them to Registrations and sends their welcome WhatsApp + email.</p>
        <div className="flex flex-wrap gap-2">
          <button data-testid="approval-approve-selected-btn" onClick={ap.approveSelected} disabled={!ap.selected.length || bulkBusy} className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2.5 text-sm hover:bg-emerald-700 disabled:opacity-50">
            <ListChecks size={16} /> {bulkBusy ? "Approving…" : `Approve selected (${ap.selected.length})`}
          </button>
          <button data-testid="approval-approve-all-btn" onClick={ap.approveAll} disabled={!ap.rows.length || bulkBusy} className="inline-flex items-center gap-2 rounded-full bg-[#1A2E26] text-white px-5 py-2.5 text-sm hover:opacity-90 disabled:opacity-50">
            <CheckCheck size={16} /> {bulkBusy ? "Approving…" : `Approve all (${ap.rows.length})`}
          </button>
        </div>
      </div>
      <div className="rounded-xl border border-border bg-white/60 p-4" data-testid="approval-table">
        {ap.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="approval-empty">No participants are waiting for approval.</p>
        ) : (
          <div className="overflow-auto max-h-[460px]">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 w-10">
                    <input type="checkbox" data-testid="approval-select-all" checked={ap.rows.length > 0 && ap.rows.every((r) => ap.selected.includes(r.id))} onChange={ap.toggleAll} className="w-4 h-4 accent-[#1A2E26] cursor-pointer" />
                  </th>
                  {COLS.map((h, i) => <th key={i} className="px-3 py-2">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {ap.rows.map((r, i) => {
                  const checked = ap.selected.includes(r.id);
                  return (
                    <tr key={r.id} data-testid={`approval-row-${i}`} className={`border-t border-border/60 transition-colors ${checked ? "bg-accent/10" : ""}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" data-testid={`approval-select-${i}`} checked={checked} onChange={() => ap.toggleSelect(r.id)} className="w-4 h-4 accent-[#1A2E26] cursor-pointer" />
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                      <td className="px-3 py-2 font-medium">{r.name}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.mobile}</td>
                      <td className="px-3 py-2 whitespace-nowrap">{r.whatsapp}</td>
                      <td className="px-3 py-2">{r.email || "—"}</td>
                      <td className="px-3 py-2">{r.profession}</td>
                      <td className="px-3 py-2">{r.place}</td>
                      <td className="px-3 py-2">{r.program}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{fmt(r.created_at)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1.5">
                          <button data-testid={`approval-approve-${i}`} onClick={() => ap.approve(r)} disabled={ap.busy === r.id || bulkBusy} title="Approve and notify" className="inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-3 py-1.5 text-xs hover:bg-emerald-700 disabled:opacity-50"><CheckCircle2 size={13} /> {ap.busy === r.id ? "…" : "Approve"}</button>
                          <button data-testid={`approval-delete-${i}`} onClick={() => ap.remove(r)} title="Remove without approving" className="grid place-items-center w-8 h-8 rounded-full border border-destructive/40 text-destructive hover:bg-destructive hover:text-white transition-colors"><Trash2 size={13} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionCard>
  );
};
